import {
  POSTHOG_IDENTITY_FLAGS,
  useIdentityFeatureFlagEnabled,
} from "utils/posthogFeatureFlags";

export const ADMIN_NAVIGATION_REDESIGN_OVERRIDE_STORAGE_KEY =
  "brev:feature-flag:admin-navigation-redesign";

type AdminNavigationRedesignOverride = boolean | "reset" | undefined;
type AdminNavigationRedesignOverrideStorage = Pick<
  Storage,
  "getItem" | "setItem" | "removeItem"
>;

export const isLocalAdminNavigationMode = (mode: string) =>
  mode.startsWith("local");

export const shouldDefaultAdminNavigationOn = (mode: string, isDev: boolean) =>
  isLocalAdminNavigationMode(mode) || (isDev && mode !== "test");

export const canUseAdminNavigationLocalOverride = (
  mode: string,
  isDev: boolean,
) => isDev && mode !== "test";

export const parseAdminNavigationRedesignOverride = (
  value?: string | null,
): AdminNavigationRedesignOverride => {
  const normalizedValue = value?.trim().toLowerCase();

  if (!normalizedValue) return undefined;
  if (["on", "true", "1"].includes(normalizedValue)) return true;
  if (["off", "false", "0"].includes(normalizedValue)) return false;
  if (normalizedValue === "reset") return "reset";

  return undefined;
};

const getBrowserSearch = () =>
  typeof window === "undefined" ? "" : window.location.search;

const getBrowserLocalStorage = ():
  | AdminNavigationRedesignOverrideStorage
  | undefined => {
  if (typeof window === "undefined") return undefined;

  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};

interface GetAdminNavigationRedesignLocalOverrideOptions {
  mode: string;
  isDev: boolean;
  search?: string;
  storage?: AdminNavigationRedesignOverrideStorage;
}

export const getAdminNavigationRedesignLocalOverride = ({
  mode,
  isDev,
  search = getBrowserSearch(),
  storage = getBrowserLocalStorage(),
}: GetAdminNavigationRedesignLocalOverrideOptions): boolean | undefined => {
  if (!canUseAdminNavigationLocalOverride(mode, isDev)) return undefined;

  const queryOverride = parseAdminNavigationRedesignOverride(
    new URLSearchParams(search).get(
      POSTHOG_IDENTITY_FLAGS.ADMIN_NAVIGATION_REDESIGN,
    ),
  );

  if (queryOverride === "reset") {
    storage?.removeItem(ADMIN_NAVIGATION_REDESIGN_OVERRIDE_STORAGE_KEY);
    return undefined;
  }

  if (typeof queryOverride === "boolean") {
    storage?.setItem(
      ADMIN_NAVIGATION_REDESIGN_OVERRIDE_STORAGE_KEY,
      queryOverride ? "on" : "off",
    );
    return queryOverride;
  }

  const storedOverride = parseAdminNavigationRedesignOverride(
    storage?.getItem(ADMIN_NAVIGATION_REDESIGN_OVERRIDE_STORAGE_KEY),
  );

  return typeof storedOverride === "boolean" ? storedOverride : undefined;
};

interface ResolveAdminNavigationRedesignEnabledOptions {
  mode: string;
  isDev: boolean;
  flagEnabled?: boolean;
  localOverride?: boolean;
}

export const resolveAdminNavigationRedesignEnabled = ({
  mode,
  isDev,
  flagEnabled,
  localOverride,
}: ResolveAdminNavigationRedesignEnabledOptions) =>
  localOverride ??
  (shouldDefaultAdminNavigationOn(mode, isDev) || !!flagEnabled);

export const useAdminNavigationRedesignEnabled = () => {
  const flagEnabled = useIdentityFeatureFlagEnabled(
    POSTHOG_IDENTITY_FLAGS.ADMIN_NAVIGATION_REDESIGN,
  );
  const mode = import.meta.env.MODE;
  const isDev = import.meta.env.DEV;
  const localOverride = getAdminNavigationRedesignLocalOverride({
    mode,
    isDev,
  });

  return resolveAdminNavigationRedesignEnabled({
    mode,
    isDev,
    flagEnabled,
    localOverride,
  });
};
